import * as React from 'react'
import { useNavigate } from 'react-router-dom'
import {
  FaIcon,
  faCalendarDaysSolid,
  faCalendarRangeSolid,
  faCalendarWeekSolid,
  faCalendarsSolid,
  faCircleInfoRegular,
  faFileRegular,
  faMagnifyingGlassSolid,
  faPeoplePantsSolid,
  faRefreshSolid,
  faTruckSolid,
  faCarSolid,
  faBellSolid,
  faTriangleExclamationRegular,
  faPrintSolid,
  faCaravanSolid,
  faTableSolid,
} from '@/lib/fa'
import Tooltip from '../ToolTip'
import DividerVertical from '../DividerVertical'
import { useActivity } from '@/modules/drivers/stores/activityStore'
import { useDate } from '@/modules/drivers/stores/dateStore'
import TmsWhite from '@/assets/icons_new/TmsWhite.svg'
import { useLocalize } from '@/modules/shared/context/localize/LocalizeContext'
import { ReportType } from '@/app/constants/reportType'
import { useGeneratePrintout } from '@/modules/printouts/hooks/useGeneratePrintout'
import { OutputType } from '@/app/constants/outputType'
import YearlyLeaveCounters from '@/modules/drivers/tabs/activities/components/YearlyLeaveCounters'
import YearlyPaletteSwitch from '@/modules/drivers/tabs/activities/components/YearlyPaletteSwitch'
import DayInfoButton from '@/modules/drivers/tabs/activities/components/DayInfoButton'
import { useIsDriverOnly } from '@/modules/shared/permissions'

const ActivitiesActionBar = ({
  basePath,
  showViolations,
  setShowViolations,
  showTachographActivities,
  setShowTachographActivities,
  showTachographEvents,
  setShowTachographEvents,
  hasVehicleSourceActivities,
  hasTachographEvents,
  onLegendClick,
  showAttestations,
  companyId,
  showTrips,
  setShowTrips,
  driverId,
  showDataHistory,
}: {
  basePath: string
  showViolations: boolean
  setShowViolations: (v: boolean) => void
  showTachographActivities: boolean
  setShowTachographActivities: (v: boolean) => void
  showTachographEvents: boolean
  setShowTachographEvents: (v: boolean) => void
  hasVehicleSourceActivities: boolean
  hasTachographEvents: boolean
  onLegendClick?: () => void
  showTrips: boolean
  showAttestations: () => void
  setShowTrips: (v: boolean) => void
  companyId: string
  driverId: string
  showDataHistory: () => void
}) => {
  const navigate = useNavigate()
  const {
    setType,
    type,
    showDefaultData,
    setShowDefaultData,
    setShowTmsData,
    setShowYearlyTrips,
    showYearlyTrips,
    showTmsData,
    vehicleTachographMode,
    setVehicleTachographMode,
    triggerRefresh,
    leaveSummary,
  } = useActivity()
  const { t } = useLocalize()
  const { getDateForView } = useDate()
  const showViolationButtons = type === 'daily' || type === 'weekly'
  const isYearly = type === 'yearly'
  // Wydruki idą przez PrintoutController (CanGeneratePrintouts) - kierowca dostałby 403.
  const isDriverOnly = useIsDriverOnly(companyId)
  const { handleGeneratePrintout, busyTooltip } = useGeneratePrintout()

  const onPrintDay = async () => {
    const day = getDateForView('daily')
    try {
      await handleGeneratePrintout({
        companyId: companyId,
        driverIds: [driverId],
        dateFrom: `${day} 00:00`,
        dateTo: `${day} 23:59`,
        selectedReports: [ReportType.DRIVER_DAILY_ACTIVITIES],
        generateToSeparateFiles: false,
        rememberSelected: false,
        outputType: OutputType.Pdf,
      })
    } catch (err) {
      console.error(err)
    }
  }

  const onGenerate = async () => {
    const yearStr = getDateForView('yearly')
    try {
      await handleGeneratePrintout({
        companyId: companyId,
        driverIds: [driverId],
        dateFrom: `${yearStr}-01-01 00:00`,
        dateTo: `${yearStr}-12-31 23:59`,
        selectedReports: [ReportType.DRIVER_CALENDAR],
        generateToSeparateFiles: false,
        rememberSelected: false,
        outputType: OutputType.Pdf,
      })
    } catch (err) {
      console.error(err)
    }
  }

  return (
    <div className="tw-flex tw-w-full tw-justify-between tw-px-4 tw-py-3.5 md:tw-items-center">
      <div className="tw-flex tw-items-end tw-gap-5">
        <Tooltip
          portal
          showDot
          checked={type === 'daily'}
          text={t('driverModule.daily-view')}
          onClick={() => {
            setType('daily')
            const dateForDaily = getDateForView('daily')
            navigate(`${basePath}/dailyActivity/${dateForDaily}`)
          }}
        >
          <FaIcon icon={faCalendarWeekSolid} className="tw-text-primary" />
        </Tooltip>

        <Tooltip
          portal
          showDot
          checked={type === 'weekly'}
          text={t('driverModule.weekly-view')}
          onClick={() => {
            setType('weekly')
            const dateForWeekly = getDateForView('weekly')
            navigate(`${basePath}/weeklyActivity/${dateForWeekly}`)
          }}
        >
          <FaIcon icon={faCalendarRangeSolid} className="tw-text-primary" />
        </Tooltip>

        <Tooltip
          portal
          showDot
          checked={type === 'monthly'}
          text={t('driverModule.monthly-view')}
          onClick={() => {
            setType('monthly')
            const dateForMonthly = getDateForView('monthly')
            navigate(`${basePath}/monthlyActivity/${dateForMonthly}`)
          }}
        >
          <FaIcon icon={faCalendarDaysSolid} className="tw-text-primary" />
        </Tooltip>

        <Tooltip
          portal
          showDot
          checked={type === 'yearly'}
          text={t('driverModule.yearly-view')}
          onClick={() => {
            setType('yearly')
            const dateForYearly = getDateForView('yearly')
            navigate(`${basePath}/yearlyActivity/${dateForYearly}`)
          }}
        >
          <FaIcon icon={faCalendarsSolid} className="tw-text-primary" />
        </Tooltip>

        <Tooltip
          portal
          text={t('driverModule.details-view')}
          checked={type === 'details'}
          showDot
          onClick={() => {
            setType('details')
            const dateForDetails = getDateForView('details')
            navigate(`${basePath}/details/${dateForDetails}`)
          }}
        >
          <FaIcon icon={faMagnifyingGlassSolid} className="tw-text-primary" />
        </Tooltip>

        <Tooltip
          portal
          showDot
          checked={type === 'chartDetails'}
          text={t('driverModule.chart-details-view')}
          onClick={() => {
            setType('chartDetails')
            const yearForChart = getDateForView('yearly')
            navigate(`${basePath}/chartDetails/${yearForChart}`)
          }}
        >
          <FaIcon icon={faTableSolid} className="tw-text-primary" />
        </Tooltip>

        {/* <Tooltip text="Edycja danych z karty kierowcy i wykresówek" disabled>
          <FaIcon icon={faChartPieSimpleSolid} className="tw-text-primary" />
        </Tooltip> */}

        {/* <Tooltip
          checked={type !== 'time'}
          text="Dostępny czas pracy kierowcy"
          disabled
          onClick={() => navigate(`${basePath}/time`)}
        >
          <FaIcon icon={faTimerSolid} className="tw-text-primary" />
        </Tooltip> */}

        <Tooltip
          portal
          checked={type === 'compare'}
          showDot
          text={t('driverModule.compare-drivers')}
          onClick={() => {
            setType('compare')
            navigate(`${basePath}/compare`)
          }}
        >
          <FaIcon icon={faPeoplePantsSolid} className="tw-text-primary" />
        </Tooltip>
        <DividerVertical />
        {isYearly ? (
          <div className="tw-flex tw-items-stretch tw-rounded-md tw-bg-[#6B77A0]/30">
            <Tooltip portal text={t('driverModule.toggle-default-data')}>
              <div
                onClick={() => setShowDefaultData(!showDefaultData)}
                className={`tw-grid tw-cursor-pointer tw-place-items-center tw-rounded-s-md tw-p-1 ${showDefaultData ? 'tw-bg-primary' : 'tw-bg-none'}`}
              >
                <FaIcon
                  icon={faFileRegular}
                  className={`tw-h-4 tw-w-4 tw-text-white`}
                />
              </div>
            </Tooltip>
            <Tooltip
              portal
              text="Pokaż/ukryj delegacje"
              onClick={() => setShowTrips(!showTrips)}
            >
              <div
                className={`tw-grid tw-cursor-pointer tw-place-items-center tw-p-1 ${showTrips ? 'tw-bg-primary' : 'tw-bg-primary/30'}`}
              >
                <FaIcon
                  icon={faCaravanSolid}
                  className="tw-h-4 tw-w-4 tw-text-white"
                />
              </div>
            </Tooltip>
            <Tooltip portal text={t('driverModule.toggle-violations')}>
              <div
                className={`tw-grid tw-cursor-pointer tw-place-items-center tw-p-1 ${showViolations ? 'tw-bg-primary' : 'tw-bg-none'}`}
                onClick={() => setShowViolations(!showViolations)}
              >
                <FaIcon
                  icon={faCircleInfoRegular}
                  className={`tw-h-4 tw-w-4 tw-text-white`}
                />
              </div>
            </Tooltip>
            <Tooltip portal text={t('driverModule.toggle-tms-data')}>
              <div
                className={`tw-grid tw-cursor-pointer tw-place-items-center tw-rounded-e-md tw-p-1 ${showTmsData ? 'tw-bg-primary' : 'tw-bg-none'}`}
                onClick={() => setShowTmsData(!showTmsData)}
              >
                <img src={TmsWhite} className={`tw-h-4 tw-w-4 tw-text-white`} />
              </div>
            </Tooltip>
          </div>
        ) : (
          <>
            {showViolationButtons && (
              <>
                <Tooltip
                  portal
                  text="Pokaż/ukryj naruszenia"
                  onClick={() => setShowViolations(!showViolations)}
                >
                  <div
                    className={`tw-grid tw-cursor-pointer tw-place-items-center tw-rounded-md tw-p-1 ${showViolations ? 'tw-bg-primary' : 'tw-bg-primary/30'}`}
                  >
                    <FaIcon
                      icon={faTriangleExclamationRegular}
                      className="tw-h-4 tw-w-4 tw-text-white"
                    />
                  </div>
                </Tooltip>

                {(type === 'daily' || type === 'weekly') &&
                  hasVehicleSourceActivities && (
                    <Tooltip
                      portal
                      text={t('driverModule.toggle-tachograph-activities')}
                      onClick={() =>
                        setShowTachographActivities(!showTachographActivities)
                      }
                    >
                      <div
                        className={`tw-grid tw-cursor-pointer tw-place-items-center tw-rounded-md tw-p-1 ${showTachographActivities ? 'tw-bg-primary' : 'tw-bg-primary/30'}`}
                      >
                        <FaIcon
                          icon={faCarSolid}
                          className="tw-h-4 tw-w-4 tw-text-white"
                        />
                      </div>
                    </Tooltip>
                  )}

                {type === 'daily' && hasTachographEvents && (
                  <Tooltip
                    portal
                    text={t('driverModule.toggle-tachograph-events')}
                    onClick={() =>
                      setShowTachographEvents(!showTachographEvents)
                    }
                  >
                    <div
                      className={`tw-grid tw-cursor-pointer tw-place-items-center tw-rounded-md tw-p-1 ${showTachographEvents ? 'tw-bg-primary' : 'tw-bg-primary/30'}`}
                    >
                      <FaIcon
                        icon={faBellSolid}
                        className="tw-h-4 tw-w-4 tw-text-white"
                      />
                    </div>
                  </Tooltip>
                )}

                {(type === 'daily' || type === 'weekly') && (
                  <Tooltip portal text={t('driverModule.toggle-auto-borders')}>
                    <div
                      onClick={() =>
                        setVehicleTachographMode(!vehicleTachographMode)
                      }
                      className={`tw-grid tw-cursor-pointer tw-place-items-center tw-rounded-md tw-p-1 ${vehicleTachographMode ? 'tw-bg-primary' : 'tw-bg-primary/30'}`}
                    >
                      <FaIcon
                        icon={faTruckSolid}
                        className="tw-h-4 tw-w-4 tw-text-white"
                      />
                    </div>
                  </Tooltip>
                )}
              </>
            )}
          </>
        )}

        <DividerVertical />
        {/* <p
          className="tw-m-0 tw-cursor-pointer tw-text-sm tw-font-medium tw-text-primary tw-underline"
          onClick={showDataHistory}
        >
         Dane z kart
        </p> */}
        <p
          className="tw-m-0 tw-cursor-pointer tw-text-sm tw-font-medium tw-text-primary tw-underline"
          onClick={showAttestations}
        >
          Lista zaświadczeń
        </p>
        {onLegendClick && (
          <p
            className="tw-m-0 tw-cursor-pointer tw-text-sm tw-font-medium tw-text-primary tw-underline"
            onClick={onLegendClick}
          >
            {t('driverModule.legend')}
          </p>
        )}
        {isYearly && <YearlyPaletteSwitch />}
        {isYearly && leaveSummary && (
          <>
            <DividerVertical />
            <YearlyLeaveCounters summary={leaveSummary} />
          </>
        )}
      </div>

      <div className="tw-flex tw-items-end tw-gap-5">
        {isYearly && !isDriverOnly && (
          <Tooltip
            portal
            text="Drukuj"
            onClick={() => onGenerate()}
            blockedText={busyTooltip}
          >
            <FaIcon icon={faPrintSolid} className="tw-text-primary" />
          </Tooltip>
        )}
        {type === 'daily' && (
          <DayInfoButton
            companyId={companyId}
            driverId={Number(driverId)}
            date={getDateForView('daily')}
            onViolationsChanged={triggerRefresh}
          />
        )}
        {type === 'daily' && !isDriverOnly && (
          <Tooltip
            portal
            text={t('driverModule.print-day')}
            onClick={() => onPrintDay()}
            blockedText={busyTooltip}
          >
            <FaIcon icon={faPrintSolid} className="tw-text-primary" />
          </Tooltip>
        )}
        <Tooltip
          portal
          text={t('driverModule.refresh')}
          onClick={() => triggerRefresh()}
        >
          <FaIcon icon={faRefreshSolid} className="tw-text-primary" />
        </Tooltip>
      </div>
    </div>
  )
}

export default ActivitiesActionBar
